/**
 * M15: Single campaign dashboard — stats, leaderboard, P2P link generator.
 */
import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { ArrowLeft, Loader2 } from "lucide-react"
import { PermissionGate } from "@/components/PermissionGate"
import { Navigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useOrgStore } from "@/stores/orgStore"
import { supabase } from "@/lib/supabase"
import type { Tables } from "@/types/database"
import { useCampaignProgress, fetchFundraiserLeaderboard, type LeaderboardEntry } from "@/hooks/useCampaignDonations"
import { CampaignProgressBar } from "@/components/campaigns/CampaignProgressBar"
import { FundraiserLeaderboard } from "@/components/campaigns/FundraiserLeaderboard"
import { GenerateFundraiserLinkModal } from "@/components/campaigns/GenerateFundraiserLinkModal"

type CampaignRow = Tables<"campaigns">

function daysRemaining(endDate: string | null): number | null {
  if (!endDate) return null
  const end = new Date(endDate + "T12:00:00")
  end.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.ceil((end.getTime() - today.getTime()) / 86400000)
}

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { currentOrg } = useOrgStore()
  const orgId = currentOrg?.id ?? null
  const orgSlug = currentOrg?.slug ?? ""
  const customDomain = currentOrg?.custom_domain ?? null

  const [campaign, setCampaign] = useState<CampaignRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
  const [linkOpen, setLinkOpen] = useState(false)

  const { progress, refresh: refreshProgress } = useCampaignProgress(id ?? null)

  useEffect(() => {
    if (!orgId || !id) {
      setCampaign(null)
      setLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const { data, error } = await supabase
        .from("campaigns")
        .select("*")
        .eq("id", id)
        .eq("org_id", orgId)
        .maybeSingle()
      if (!cancelled) {
        if (error || !data) setCampaign(null)
        else setCampaign(data as CampaignRow)
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [orgId, id])

  useEffect(() => {
    if (!id) return
    let cancelled = false
    ;(async () => {
      try {
        const rows = await fetchFundraiserLeaderboard(id)
        if (!cancelled) setLeaderboard(rows)
      } catch {
        if (!cancelled) setLeaderboard([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [id, progress?.raised_cents])

  const ended = Boolean(
    campaign?.end_date &&
      (() => {
        const end = new Date(campaign.end_date + "T12:00:00")
        end.setHours(0, 0, 0, 0)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return end < today
      })()
  )

  const days = campaign ? daysRemaining(campaign.end_date) : null

  return (
    <PermissionGate permission="can_manage_content" fallback={<Navigate to="/dashboard" replace />}>
      <div className="space-y-6">
        <Button variant="ghost" size="sm" asChild className="-ml-2">
          <Link to="/dashboard/campaigns" className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to campaigns
          </Link>
        </Button>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !campaign ? (
          <p className="text-destructive">Campaign not found.</p>
        ) : (
          <>
            <div>
              <h1 className="text-2xl font-bold">{campaign.title}</h1>
              <p className="text-sm text-muted-foreground">
                {ended
                  ? "Ended"
                  : days != null && days >= 0
                    ? `${days} days remaining`
                    : "No end date set"}
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Progress</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <CampaignProgressBar
                  raisedCents={progress?.raised_cents ?? 0}
                  goalCents={campaign.goal_cents}
                  ended={Boolean(ended)}
                />
                <div className="grid gap-4 sm:grid-cols-3 text-sm">
                  <div>
                    <div className="text-muted-foreground">Total raised</div>
                    <div className="text-lg font-semibold tabular-nums">
                      {((progress?.raised_cents ?? 0) / 100).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 0,
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Donors</div>
                    <div className="text-lg font-semibold">{progress?.donor_count ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Fundraisers</div>
                    <div className="text-lg font-semibold">{progress?.fundraiser_count ?? 0}</div>
                  </div>
                </div>
                <Button type="button" onClick={() => setLinkOpen(true)}>
                  Generate fundraising link
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Peer-to-peer leaderboard</CardTitle>
              </CardHeader>
              <CardContent>
                <FundraiserLeaderboard entries={leaderboard} />
              </CardContent>
            </Card>

            {orgId && (
              <GenerateFundraiserLinkModal
                open={linkOpen}
                onOpenChange={setLinkOpen}
                orgId={orgId}
                org={{ slug: orgSlug, custom_domain: customDomain }}
                defaultCampaignId={campaign.id}
                onCreated={() => {
                  void refreshProgress()
                  void fetchFundraiserLeaderboard(campaign.id).then(setLeaderboard)
                }}
              />
            )}
          </>
        )}
      </div>
    </PermissionGate>
  )
}
